// Cuántas veces el scoring rechazó a un acreditado en la ventana reciente, a partir de su historial
// de evaluaciones SCI — lógica PURA, sin React y sin fetch.
import { ScoringDecisionEnum } from '@fiado/type-kit/bin/loanScoring/enums/ScoringDecisionEnum';
import type { SciEvaluationResponse } from '@fiado/type-kit/bin/loanScoring/dtos/responses/SciEvaluationResponse';
import { daysBetweenDays, mxDay } from '@/lib/dateHelpers';
import type { CustomerCreditFacts } from './customerCreditFacts';

/** La ventana de la fila «Rechazos 30d» del mockup, en días de calendario del piso de venta. */
export const REJECTIONS_WINDOW_DAYS = 30;

/** El historial del acreditado, o el motivo por el que no se pudo leer. Se resuelve en el `queryFn`
 *  para que un 403 de una lectura ACCESORIA no dispare el toast global de la app. */
export type SciEvaluationsRead =
    | { state: 'ready'; evaluations: SciEvaluationResponse[] }
    | { state: 'forbidden' }
    | { state: 'error' };

/** Por qué no hay conteo de rechazos. Ninguno de los cinco es un cero. */
export type UnreadRejectionsState =
    | 'loading'
    | 'no-customer'
    | 'no-borrower'
    | 'forbidden'
    | 'error';

/** El conteo de rechazos, o el motivo por el que no lo hay. Nunca las dos cosas a la vez. */
export type BorrowerRejectionsFacts =
    | { count: number; unreadState: null }
    | { count: null; unreadState: UnreadRejectionsState };

/**
 * Rechazos del scoring dentro de la ventana, en días de calendario de CDMX. `NO_ELIGIBLE` no cuenta:
 * es «no alcanzó el piso», no un rechazo, y el mockup pide la fila de rechazos.
 */
export function countRecentRejections(evaluations: SciEvaluationResponse[], today: string): number {
    return evaluations.filter(
        (evaluation) =>
            evaluation.decision === ScoringDecisionEnum.REJECTED &&
            // La fechada en el futuro por desfase de reloj entra: es más nueva que hoy, no más
            // vieja que la ventana.
            daysBetweenDays(today, mxDay(evaluation.createdAt)) < REJECTIONS_WINDOW_DAYS,
    ).length;
}

/** La cartera sin celda no dice si el cliente tiene acreditado: el hueco de los rechazos hereda el
 *  suyo en vez de inventar un «no tiene». */
function stateOfUnreadCredit(facts: Extract<CustomerCreditFacts, { entry: null }>) {
    if (facts.unreadState === 'loading') return 'loading' as const;
    return facts.unreadState === 'forbidden' ? ('forbidden' as const) : ('error' as const);
}

export interface BorrowerRejectionsInput {
    /** El prospecto ya está registrado como cliente. Sin eso no hay expediente que consultar. */
    hasCustomer: boolean;
    /** La cartera del cliente, de donde sale el `borrowerId`, o su motivo de hueco. */
    credit: CustomerCreditFacts;
    /** El rol puede leer `loan.scoring.read`. Sin él la lectura ni se intenta. */
    canRead: boolean;
    /** El historial del acreditado. `undefined` es que todavía viaja. */
    read: SciEvaluationsRead | undefined;
    /** Día de calendario del piso de venta. `''` es que todavía no se calculó. */
    today: string;
}

/**
 * El conteo de rechazos con su cadena de huecos, de lo más específico a lo más general. Un cero
 * LEÍDO es dato afirmado y sale como `count: 0`; ninguno de los motivos se pinta como cero.
 */
export function borrowerRejectionsFactsOf(input: BorrowerRejectionsInput): BorrowerRejectionsFacts {
    if (!input.hasCustomer) return { count: null, unreadState: 'no-customer' };
    if (!input.credit.entry) {
        return { count: null, unreadState: stateOfUnreadCredit(input.credit) };
    }
    if (!input.credit.entry.borrowerId) return { count: null, unreadState: 'no-borrower' };
    if (!input.canRead) return { count: null, unreadState: 'forbidden' };
    if (!input.read || !input.today) return { count: null, unreadState: 'loading' };
    if (input.read.state !== 'ready') return { count: null, unreadState: input.read.state };
    return { count: countRecentRejections(input.read.evaluations, input.today), unreadState: null };
}
